import Dexie, { type Table } from 'dexie';

/** Local clinical vault foundation; it does not install a PWA or grant offline clinical authority. */
export type OfflineIdentity = { organizationId: string; userId: string; deviceId: string };
export type OfflineEnvelope<T = unknown> = OfflineIdentity & {
  id: string;
  clientSeq: number;
  clientRecordedAt: string;
  payload: T;
};
export type PendingState =
  | 'pending'
  | 'sign-in-required'
  | 'retryable-error'
  | 'receipt-mismatch'
  | 'forbidden'
  | 'conflict'
  | 'invalid'
  | 'held';
export type OfflineReceipt = { id: string; deviceId: string; clientSeq: number; payloadHash: string };
export type OfflineSender<T = unknown> = (envelope: OfflineEnvelope<T>, payloadHash: string) => Promise<OfflineReceipt>;
export type PendingOperation<T = unknown> = {
  envelope: OfflineEnvelope<T>;
  payloadHash: string;
  state: PendingState;
  attempts: number;
  nextAttemptAt?: number;
};
export type OfflineStatus = {
  locked: boolean;
  syncing: boolean;
  pending: number;
  signInRequired: number;
  retryableErrors: number;
  receiptMismatches: number;
  blocked?: number;
  nextAttemptAt?: number;
  recoveryOnly?: boolean;
};

type VaultRow = {
  scope: string;
  salt: Uint8Array;
  iv: Uint8Array;
  wrappedKey: ArrayBuffer;
  iterations: number;
  nextSeq: number;
  recoveryOnly?: boolean;
};
type CipherRow = {
  scope: string;
  id: string;
  clientSeq: number;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
  payloadHash: string;
};
type SnapshotRow = { scope: string; id: string; iv: Uint8Array; ciphertext: ArrayBuffer; revision?: number };
type OutboxRow = { scope: string; id: string; state: PendingState; attempts: number; nextAttemptAt?: number };
const ITERATIONS = 600_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

class VaultDatabase extends Dexie {
  vaults!: Table<VaultRow, string>;
  events!: Table<CipherRow, [string, string]>;
  outbox!: Table<OutboxRow, [string, string]>;
  snapshots!: Table<SnapshotRow, [string, string]>;
  constructor(name: string) {
    super(name);
    this.version(1).stores({
      vaults: 'scope',
      events: '[scope+id],scope,&[scope+clientSeq]',
      outbox: '[scope+id],scope',
    });
    this.version(2).stores({ snapshots: '[scope+id],scope' });
  }
}

function bytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  return new Uint8Array(value);
}
function random(size: number) {
  return crypto.getRandomValues(new Uint8Array(size));
}
function context(scope: string, id: string) {
  return encoder.encode(JSON.stringify(['flowsheet-vault-v1', scope, id]));
}
function hex(value: ArrayBuffer) {
  return Array.from(new Uint8Array(value), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
async function wrappingKey(passphrase: string, salt: Uint8Array, iterations: number) {
  const material = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: bytes(salt), iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}
function isUnauthorized(error: unknown) {
  const value = error as { status?: number; response?: { status?: number } } | null;
  return value?.status === 401 || value?.response?.status === 401;
}

export class OfflineVault {
  private key: CryptoKey | null;
  private syncing = false;
  private constructor(
    private db: VaultDatabase,
    private identity: OfflineIdentity,
    private scope: string,
    key: CryptoKey,
  ) {
    this.key = key;
  }

  /** The passphrase is separate from the login token. No passphrase/raw key is persisted. */
  static async open(options: OfflineIdentity & { passphrase: string; databaseName?: string }): Promise<OfflineVault> {
    const { organizationId, userId, deviceId, passphrase } = options;
    if (![organizationId, userId, deviceId].every((value) => value.trim().length > 0))
      throw new Error('Vault identity is required.');
    if (passphrase.length < 12) throw new Error('Use an offline passphrase of at least 12 characters.');
    const identity = { organizationId, userId, deviceId };
    const scope = JSON.stringify([organizationId, userId, deviceId]);
    const db = new VaultDatabase(options.databaseName ?? 'mohair-flowsheets-vault');
    try {
      let row = await db.vaults.get(scope);
      if (!row) {
        const salt = random(16);
        const iv = random(12);
        const kek = await wrappingKey(passphrase, salt, ITERATIONS);
        const newKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
        const wrappedKey = await crypto.subtle.wrapKey('raw', newKey, kek, {
          name: 'AES-GCM',
          iv,
          additionalData: context(scope, 'key'),
        });
        const candidate: VaultRow = { scope, salt, iv, wrappedKey, iterations: ITERATIONS, nextSeq: 1 };
        // Another tab may provision the vault while key generation is running.
        await db.transaction('rw', db.vaults, async () => {
          if (!(await db.vaults.get(scope))) await db.vaults.add(candidate);
        });
        row = await db.vaults.get(scope);
      }
      if (!row || row.iterations !== ITERATIONS) throw new Error('Unsupported vault key format.');
      const kek = await wrappingKey(passphrase, row.salt, row.iterations);
      const key = await crypto.subtle.unwrapKey(
        'raw',
        row.wrappedKey,
        kek,
        { name: 'AES-GCM', iv: bytes(row.iv), additionalData: context(scope, 'key') },
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      );
      return new OfflineVault(db, identity, scope, key);
    } catch (error) {
      db.close();
      throw error;
    }
  }

  /** Export ciphertext and the wrapped key; the original passphrase is still required. */
  async exportRecovery(): Promise<string> {
    this.unlocked();
    const data = await this.db.transaction(
      'r',
      this.db.vaults,
      this.db.events,
      this.db.outbox,
      this.db.snapshots,
      async () => ({
        identity: this.identity,
        vault: await this.db.vaults.get(this.scope),
        events: await this.db.events.where('scope').equals(this.scope).toArray(),
        outbox: await this.db.outbox.where('scope').equals(this.scope).toArray(),
        snapshots: await this.db.snapshots.where('scope').equals(this.scope).toArray(),
      }),
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(JSON.stringify(data, recoveryBinary)),
    );
    const proof = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: bytes(iv), additionalData: context(this.scope, 'recovery-manifest') },
      this.unlocked(),
      digest,
    );
    return JSON.stringify(
      { format: 'mohair-offline-recovery', version: 1, data, manifest: { iv, proof } },
      recoveryBinary,
    );
  }
  /** Restore only into an absent vault. New writes are disabled to avoid cloned device sequences. */
  static async restoreRecovery(
    file: string,
    passphrase: string,
    databaseName = 'mohair-flowsheets-vault',
  ): Promise<OfflineIdentity> {
    if (file.length > 50_000_000) throw new Error('Recovery file is too large.');
    const bundle = JSON.parse(file, (_name, value) => {
      if (
        value &&
        typeof value === 'object' &&
        ['buffer', 'bytes'].includes(value.binaryType) &&
        typeof value.base64 === 'string'
      ) {
        const array = Uint8Array.from(atob(value.base64), (character) => character.charCodeAt(0));
        return value.binaryType === 'buffer' ? array.buffer : array;
      }
      return value;
    });
    if (bundle.format !== 'mohair-offline-recovery' || bundle.version !== 1 || !bundle.data)
      throw new Error('Unsupported recovery file.');
    const {
      identity,
      vault: row,
      events,
      outbox,
      snapshots,
    } = bundle.data as {
      identity: OfflineIdentity;
      vault: VaultRow;
      events: CipherRow[];
      outbox: OutboxRow[];
      snapshots: SnapshotRow[];
    };
    if (
      !identity ||
      ![identity.organizationId, identity.userId, identity.deviceId].every(
        (value) => typeof value === 'string' && value.length > 0,
      )
    )
      throw new Error('Invalid recovery identity.');
    const scope = JSON.stringify([identity.organizationId, identity.userId, identity.deviceId]);
    if (
      !row ||
      row.scope !== scope ||
      row.iterations !== ITERATIONS ||
      !Number.isSafeInteger(row.nextSeq) ||
      !Array.isArray(events) ||
      !Array.isArray(outbox) ||
      !Array.isArray(snapshots)
    )
      throw new Error('Invalid recovery structure.');
    const kek = await wrappingKey(passphrase, row.salt, row.iterations);
    const key = await crypto.subtle.unwrapKey(
      'raw',
      row.wrappedKey,
      kek,
      { name: 'AES-GCM', iv: bytes(row.iv), additionalData: context(scope, 'key') },
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    if (!bundle.manifest?.iv || !bundle.manifest?.proof) throw new Error('Recovery manifest is missing.');
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(JSON.stringify(bundle.data, recoveryBinary)),
    );
    const verified = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bytes(bundle.manifest.iv), additionalData: context(scope, 'recovery-manifest') },
      key,
      bundle.manifest.proof,
    );
    if (hex(digest) !== hex(verified)) throw new Error('Recovery manifest integrity check failed.');
    const db = new VaultDatabase(databaseName);
    const instance = new OfflineVault(db, identity, scope, key);
    try {
      const ids = new Set<string>();
      for (const event of events) {
        if (
          event.scope !== scope ||
          ids.has(event.id) ||
          !Number.isSafeInteger(event.clientSeq) ||
          event.clientSeq < 1 ||
          event.clientSeq >= row.nextSeq
        )
          throw new Error('Invalid recovery event identity.');
        ids.add(event.id);
        await instance.decrypt(event);
      }
      for (const record of outbox)
        if (
          record.scope !== scope ||
          !ids.has(record.id) ||
          ![
            'pending',
            'sign-in-required',
            'retryable-error',
            'receipt-mismatch',
            'forbidden',
            'conflict',
            'invalid',
            'held',
          ].includes(record.state)
        )
          throw new Error('Invalid recovery pending entry.');
      for (const snapshot of snapshots) {
        if (snapshot.scope !== scope) throw new Error('Invalid recovery snapshot identity.');
        await instance.decodeSnapshot(snapshot);
      }
      await db.transaction('rw', db.vaults, db.events, db.outbox, db.snapshots, async () => {
        if (await db.vaults.get(scope))
          throw new Error('This workspace already exists. Recovery never overwrites an existing workspace.');
        await db.vaults.add({ ...row, recoveryOnly: true });
        await db.events.bulkAdd(events);
        await db.outbox.bulkAdd(outbox);
        await db.snapshots.bulkAdd(snapshots);
      });
      return identity;
    } finally {
      instance.close();
    }
  }

  /** Opaque account identifiers only; clinical labels stay encrypted until unlock. */
  static async listPreparedIdentities(databaseName = 'mohair-flowsheets-vault'): Promise<OfflineIdentity[]> {
    const db = new VaultDatabase(databaseName);
    try {
      const rows = await db.vaults.toArray();
      return rows.map((row) => {
        const [organizationId, userId, deviceId] = JSON.parse(row.scope) as string[];
        return { organizationId, userId, deviceId };
      });
    } finally {
      db.close();
    }
  }
  async listSnapshots<T>(prefix: string): Promise<{ id: string; value: T }[]> {
    this.unlocked();
    const rows = await this.db.snapshots.where('scope').equals(this.scope).toArray();
    const values = await Promise.all(
      rows
        .filter((row) => row.id.startsWith(prefix))
        .map(async (row) => ({ id: row.id, value: await this.getSnapshot<T>(row.id) })),
    );
    return values.filter((row) => row.value !== undefined) as { id: string; value: T }[];
  }

  lock(): void {
    this.key = null;
  }
  close(): void {
    this.lock();
    this.db.close();
  }
  private unlocked(): CryptoKey {
    if (!this.key) throw new Error('Offline vault is locked.');
    return this.key;
  }
  private assertKey(key: CryptoKey): void {
    if (this.key !== key) throw new Error('Offline vault is locked.');
  }

  private async decrypt<T>(row: CipherRow): Promise<OfflineEnvelope<T>> {
    const key = this.unlocked();
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bytes(row.iv), additionalData: context(this.scope, row.id) },
      key,
      row.ciphertext,
    );
    const hash = hex(await crypto.subtle.digest('SHA-256', plaintext));
    this.assertKey(key);
    if (hash !== row.payloadHash) throw new Error('Stored event integrity check failed.');
    const envelope = JSON.parse(decoder.decode(plaintext)) as OfflineEnvelope<T>;
    if (
      envelope.id !== row.id ||
      envelope.clientSeq !== row.clientSeq ||
      envelope.organizationId !== this.identity.organizationId ||
      envelope.userId !== this.identity.userId ||
      envelope.deviceId !== this.identity.deviceId
    )
      throw new Error('Stored event identity check failed.');
    return envelope;
  }

  /** Clinical occurred_at belongs inside payload; device time is independently captured here. */
  async enqueue<T>(operation: { id: string; payload: T }): Promise<OfflineEnvelope<T>> {
    const key = this.unlocked();
    if (!operation.id.trim()) throw new Error('Event identity is required.');
    // Snapshot caller-owned data before asynchronous work; reject non-JSON payloads.
    const payloadText = JSON.stringify(operation.payload);
    if (payloadText === undefined) throw new Error('Event payload must be JSON.');
    const payload = JSON.parse(payloadText) as T;
    for (let contention = 0; contention < 20; contention++) {
      const existing = await this.db.events.get([this.scope, operation.id]);
      if (existing) {
        const original = await this.decrypt<T>(existing);
        if (JSON.stringify(original.payload) !== payloadText)
          throw new Error('Event ID already exists with different data.');
        return original;
      }
      const vault = await this.db.vaults.get(this.scope);
      if (!vault) throw new Error('Offline vault is unavailable.');
      if (vault.recoveryOnly)
        throw new Error(
          'Recovery workspaces can synchronize retained entries only. Prepare a new workspace online for new observations.',
        );
      const envelope: OfflineEnvelope<T> = {
        ...this.identity,
        id: operation.id,
        clientSeq: vault.nextSeq,
        clientRecordedAt: new Date().toISOString(),
        payload,
      };
      const serialized = encoder.encode(JSON.stringify(envelope));
      const iv = random(12);
      const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, additionalData: context(this.scope, operation.id) },
        key,
        serialized,
      );
      const payloadHash = hex(await crypto.subtle.digest('SHA-256', serialized));
      this.assertKey(key);
      const written = await this.db.transaction('rw', this.db.vaults, this.db.events, this.db.outbox, async () => {
        this.assertKey(key);
        const current = await this.db.vaults.get(this.scope);
        if (!current || current.nextSeq !== vault.nextSeq || (await this.db.events.get([this.scope, operation.id])))
          return false;
        await this.db.events.add({
          scope: this.scope,
          id: operation.id,
          clientSeq: envelope.clientSeq,
          iv,
          ciphertext,
          payloadHash,
        });
        await this.db.outbox.add({ scope: this.scope, id: operation.id, state: 'pending', attempts: 0 });
        await this.db.vaults.update(this.scope, { nextSeq: envelope.clientSeq + 1 });
        this.assertKey(key);
        return true;
      });
      if (written) return envelope;
    }
    throw new Error('Concurrent local saves are busy; retry this entry.');
  }

  /** Compare-and-swap keeps event pages and cursor changes atomic across tabs/requests. */
  async updateSnapshot<T>(id: string, update: (previous: T | undefined) => T): Promise<void> {
    const key = this.unlocked();
    for (let attempt = 0; attempt < 20; attempt++) {
      const previous = await this.db.snapshots.get([this.scope, id]);
      const decoded = previous ? await this.decodeSnapshot<T>(previous) : undefined;
      const encoded = encoder.encode(JSON.stringify(update(decoded)));
      const iv = random(12);
      const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, additionalData: context(this.scope, `snapshot:${id}`) },
        key,
        encoded,
      );
      this.assertKey(key);
      const committed = await this.db.transaction('rw', this.db.snapshots, async () => {
        const current = await this.db.snapshots.get([this.scope, id]);
        if ((current?.revision ?? 0) !== (previous?.revision ?? 0) || !!current !== !!previous) return false;
        this.assertKey(key);
        await this.db.snapshots.put({ scope: this.scope, id, iv, ciphertext, revision: (previous?.revision ?? 0) + 1 });
        return true;
      });
      if (committed) return;
    }
    throw new Error('Concurrent cache update is busy; retry synchronization.');
  }
  async putSnapshot<T>(id: string, value: T): Promise<void> {
    await this.updateSnapshot<T>(id, () => value);
  }
  private async decodeSnapshot<T>(row: SnapshotRow): Promise<T> {
    const key = this.unlocked();
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bytes(row.iv), additionalData: context(this.scope, `snapshot:${row.id}`) },
      key,
      row.ciphertext,
    );
    this.assertKey(key);
    return JSON.parse(decoder.decode(plaintext)) as T;
  }
  async getSnapshot<T>(id: string): Promise<T | undefined> {
    this.unlocked();
    const row = await this.db.snapshots.get([this.scope, id]);
    return row ? this.decodeSnapshot<T>(row) : undefined;
  }

  async listEvents<T = unknown>(): Promise<OfflineEnvelope<T>[]> {
    this.unlocked();
    const rows = await this.db.events.where('scope').equals(this.scope).sortBy('clientSeq');
    return Promise.all(rows.map((row) => this.decrypt<T>(row)));
  }
  async listPending<T = unknown>(): Promise<PendingOperation<T>[]> {
    this.unlocked();
    const rows = await this.db.outbox.where('scope').equals(this.scope).toArray();
    const operations = await Promise.all(
      rows.map(async (row) => {
        const stored = await this.db.events.get([this.scope, row.id]);
        if (!stored) throw new Error('Pending event is unavailable; recovery is required.');
        return {
          envelope: await this.decrypt<T>(stored),
          payloadHash: stored.payloadHash,
          state: row.state,
          attempts: row.attempts,
          nextAttemptAt: row.nextAttemptAt,
        };
      }),
    );
    return operations.sort((a, b) => a.envelope.clientSeq - b.envelope.clientSeq);
  }
  async status(): Promise<OfflineStatus> {
    const pending = await this.db.outbox.where('scope').equals(this.scope).toArray();
    const vault = await this.db.vaults.get(this.scope);
    return {
      recoveryOnly: vault?.recoveryOnly ?? false,
      locked: !this.key,
      syncing: this.syncing,
      pending: pending.length,
      signInRequired: pending.filter((row) => row.state === 'sign-in-required').length,
      retryableErrors: pending.filter((row) => row.state === 'retryable-error').length,
      receiptMismatches: pending.filter((row) => row.state === 'receipt-mismatch').length,
      blocked: pending.filter((row) =>
        ['forbidden', 'conflict', 'invalid', 'held', 'receipt-mismatch'].includes(row.state),
      ).length,
      nextAttemptAt: pending.some((row) => row.state === 'retryable-error')
        ? Math.min(...pending.filter((row) => row.state === 'retryable-error').map((row) => row.nextAttemptAt ?? 0))
        : undefined,
    };
  }

  /** Retains the immutable original; resolution notes are encrypted, never plaintext outbox metadata. */
  async resolveOperation(id: string, action: 'retry' | 'hold', reason: string): Promise<void> {
    this.unlocked();
    if (!reason.trim()) throw new Error('A review reason is required.');
    const row = await this.db.outbox.get([this.scope, id]);
    if (!row) throw new Error('Pending batch not found.');
    await this.putSnapshot(`resolution:${id}:${crypto.randomUUID()}`, {
      id,
      action,
      reason: reason.trim(),
      recordedAt: new Date().toISOString(),
      priorState: row.state,
    });
    await this.db.outbox.update([this.scope, id], { state: action === 'hold' ? 'held' : 'pending', nextAttemptAt: 0 });
  }

  /** Caller must bind sender to the same authenticated tenant/user and re-check permissions. */
  async sync<T = unknown>(
    sender: OfflineSender<T>,
    options: { automatic?: boolean; now?: () => number } = {},
  ): Promise<OfflineStatus> {
    const key = this.unlocked();
    if (this.syncing) return this.status();
    this.syncing = true;
    try {
      for (const operation of await this.listPending<T>()) {
        this.assertKey(key);
        if (
          operation.state === 'held' ||
          (options.automatic &&
            (!['pending', 'retryable-error'].includes(operation.state) ||
              (operation.nextAttemptAt ?? 0) > (options.now ?? Date.now)()))
        )
          continue;
        const { envelope, payloadHash } = operation;
        const rowKey: [string, string] = [this.scope, envelope.id];
        await this.db.outbox.update(rowKey, { attempts: operation.attempts + 1 });
        let receipt: OfflineReceipt;
        try {
          receipt = await sender(envelope, payloadHash);
        } catch (error) {
          this.assertKey(key);
          const status =
            (error as { status?: number; response?: { status?: number } })?.status ??
            (error as { response?: { status?: number } })?.response?.status;
          const state: PendingState = isUnauthorized(error)
            ? 'sign-in-required'
            : (error as { code?: string })?.code === 'RECEIPT_MISMATCH'
              ? 'receipt-mismatch'
              : status === 403
                ? 'forbidden'
                : status === 409
                  ? 'conflict'
                  : status === 400 || status === 422
                    ? 'invalid'
                    : 'retryable-error';
          const delay = Math.min(300_000, 1000 * 2 ** Math.min(operation.attempts, 8));
          await this.db.outbox.update(rowKey, { state, nextAttemptAt: (options.now ?? Date.now)() + delay });
          if (state === 'retryable-error' || state === 'sign-in-required') break;
          continue; // Rejected batches remain visible without blocking unrelated valid entries.
        }
        this.assertKey(key);
        if (
          !receipt ||
          receipt.id !== envelope.id ||
          receipt.deviceId !== envelope.deviceId ||
          receipt.clientSeq !== envelope.clientSeq ||
          receipt.payloadHash !== payloadHash
        ) {
          await this.db.outbox.update(rowKey, { state: 'receipt-mismatch' });
          break;
        }
        await this.db.outbox.delete(rowKey); // Preserve encrypted clinical record after acknowledgment.
      }
    } finally {
      this.syncing = false;
    }
    return this.status();
  }
}
export const openOfflineVault = OfflineVault.open;

function recoveryBinary(_name: string, value: unknown) {
  if (value instanceof ArrayBuffer || value instanceof Uint8Array) {
    const values = value instanceof ArrayBuffer ? new Uint8Array(value) : value;
    return {
      binaryType: value instanceof ArrayBuffer ? 'buffer' : 'bytes',
      base64: btoa(Array.from(values, (byte) => String.fromCharCode(byte)).join('')),
    };
  }
  return value;
}
